/**
 * Where in the app we are, as a hash.
 *
 * Hash routing rather than history routing: the built app is served as static
 * files behind nginx (M6), and a hash needs no rewrite rule to survive a
 * refresh on `#/post/abc`.
 */

export type Route =
  | { readonly screen: 'island'; readonly day: string | null }
  | { readonly screen: 'letter' }
  | { readonly screen: 'post'; readonly publicId: string | null }
  | { readonly screen: 'atlas' }
  | { readonly screen: 'day'; readonly day: string | null }
  | { readonly screen: 'auth' }
  /** The code of the Guild (#68): the one screen besides the landing a visitor may read. */
  | { readonly screen: 'code' }

export type ScreenId = Route['screen']

/** The screens the navigation offers, in the order it offers them. */
export const NAV_SCREENS = ['island', 'letter', 'post', 'atlas', 'day'] as const

/** The `ui` key naming each screen, as the header says it on a desk. */
export const NAV_KEYS: Readonly<Record<(typeof NAV_SCREENS)[number], string>> = {
  island: 'navIsland',
  letter: 'navLetter',
  post: 'navPost',
  atlas: 'navAtlas',
  day: 'navDayEnd',
}

const DAY = /^\d{4}-\d{2}-\d{2}$/

export function parseRoute(hash: string): Route {
  const [screen, argument] = hash.replace(/^#\/?/, '').split('/')
  switch (screen) {
    case 'letter':
      return { screen: 'letter' }
    case 'post':
      // An empty segment is no argument: `#/post/` names no letter, and an
      // empty id asked the server for `/api/post/` and got a refusal over
      // the list of envelopes (#117).
      return {
        screen: 'post',
        publicId: argument === undefined || argument === '' ? null : argument,
      }
    case 'atlas':
      return { screen: 'atlas' }
    case 'day':
      return { screen: 'day', day: argument !== undefined && DAY.test(argument) ? argument : null }
    case 'auth':
      return { screen: 'auth' }
    case 'code':
      return { screen: 'code' }
    default:
      return {
        screen: 'island',
        day: argument !== undefined && DAY.test(argument) ? argument : null,
      }
  }
}

export function routePath(route: Route): string {
  switch (route.screen) {
    case 'island':
      return route.day === null ? '#/island' : `#/island/${route.day}`
    case 'post':
      return route.publicId === null ? '#/post' : `#/post/${route.publicId}`
    case 'day':
      return route.day === null ? '#/day' : `#/day/${route.day}`
    default:
      return `#/${route.screen}`
  }
}

/**
 * The same five, short, for the strip a phone gets (#128, Q24.1).
 *
 * At 360 px the long keys put two of the five on screen and the rest past the
 * edge, and a strip a player has to notice scrolls is one half of them will
 * not. The short labels say what the long ones say, in fewer letters.
 */
export const NAV_KEYS_SHORT: Readonly<Record<(typeof NAV_SCREENS)[number], string>> = {
  island: 'navIslandShort',
  letter: 'navLetterShort',
  post: 'navPostShort',
  atlas: 'navAtlasShort',
  day: 'navDayEndShort',
}
